import { Box, Chip, Divider, Typography } from '@mui/material';
import { theme } from '@store';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import TextsmsIcon from '@mui/icons-material/Textsms';
import PersonIcon from '@mui/icons-material/Person';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';

import { AddOutreachAttemptModal } from './AddOutreachAttemptModal';
import { paths } from '@common/types';
import dayjs from 'dayjs';
import { FAILED_OUTREACH_ATTEMPTS_UNTIL_COMPLETION } from '@common/types/constants.ts';

interface IAttemptsProps {
  task: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json'];
}

export const Attempts = ({ task }: IAttemptsProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '1rem',
      }}
    >
      {task.outreach_attempts.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            bgcolor: theme.palette.grey[50],
            padding: '0.5rem',
            px: '1rem',
            width: '100%',
            border: '1px solid',
            borderColor: theme.palette.grey[300],
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            No failed outreach attempts added
          </Typography>
          <AddOutreachAttemptModal
            task_id={task.id}
            disabled={!task.has_write_permission}
            buttonProps={{
              variant: 'outlined',
              startIcon: <></>,
            }}
          />
        </Box>
      ) : (
        <>
          {task.outreach_attempts.map((attempt, index) => (
            <AttemptEntry key={`${index}-${attempt.notes}`} attempt={attempt} />
          ))}

          <AddOutreachAttemptModal
            task_id={task.id}
            disabled={
              task.outreach_attempts.length >=
                FAILED_OUTREACH_ATTEMPTS_UNTIL_COMPLETION ||
              !task.has_write_permission
            }
          />
        </>
      )}
    </Box>
  );
};

interface IAttemptEntryProps {
  attempt: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json']['outreach_attempts'][0];
}

const methodToIconMapper = (
  attempt: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json']['outreach_attempts'][0],
) => {
  const iconProps = {
    sx: { width: '24px', height: '24px' },
    htmlColor: theme.palette.primary.main,
  };

  switch (attempt.outreach_method) {
    case 'Phone Call':
      return <PhoneIcon {...iconProps} />;
    case 'Email':
      return <EmailIcon {...iconProps} />;
    case 'Text Message':
      return <TextsmsIcon {...iconProps} />;
    case 'In Person':
      return <PersonIcon {...iconProps} />;
    case 'Letter':
      return <MarkunreadMailboxIcon {...iconProps} />;
    default:
      return null;
  }
};

const AttemptEntry = ({ attempt }: IAttemptEntryProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        bgcolor: theme.palette.grey[50],
        padding: '1rem',
        width: '100%',
        gap: '1rem',
        border: '1px solid',
        borderColor: theme.palette.grey[300],
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {methodToIconMapper(attempt)}
          <Typography
            sx={{ wordBreak: 'break-word', lineHeight: '24px' }}
            variant='subtitle2'
          >
            {attempt.outreach_method}{' '}
            {attempt.outreach_disposition
              ? `(${attempt.outreach_disposition})`
              : ''}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant='body1'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {dayjs(attempt.attempt_date).format('MMM DD, YYYY')}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <Typography
          variant='body1'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          Follow-up to date:{' '}
        </Typography>
        <Typography variant='body1'>
          {dayjs(attempt.new_latest_date).format('MMM DD, YYYY')}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ width: '100%', display: 'flex' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Typography
              variant='body1'
              sx={{ color: theme.palette.semiBlack.main }}
            >
              Notes:
            </Typography>
            <Typography variant='body1'>{attempt.notes}</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Typography
              variant='body1'
              sx={{ color: theme.palette.semiBlack.main }}
            >
              Minutes:
            </Typography>
            <Typography variant='body1'>{attempt.recorded_minutes}</Typography>
          </Box>
        </Box>
        <Box>
          {attempt.notable_call && (
            <Chip
              label={'Notable'}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme => theme.palette.common.white,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
